// based on https://github.com/jaredLunde/react-hook/blob/master/packages/size/src/index.tsx

import useLayoutEffect from "@react-hook/passive-layout-effect";
import useResizeObserver from "@react-hook/resize-observer";
import _ from "lodash";
import { IObservableValue, observable } from "mobx";
import * as React from "react";
import { useMemo } from "react";

/**
 * A React hook for measuring the size of HTML elements including when they change
 *
 * @param target A React ref created by `useRef()` or an HTML element
 * @param options Configures the initial width and initial height of the hook's state
 */
const useBreakpoint = <T extends HTMLElement, F>(
  target: React.RefObject<T> | T | null,
  breakpoint: (width: number, height: number) => F,
  options?: UseBreakpointOptions
): F => {
  const [state, setState] = React.useState<F>(() => {
    const targetEl: any =
      target && "current" in target ? target.current : target;
    return targetEl
      ? breakpoint(
          // + getScrollbarWidth to prevent resize bonanza
          targetEl.offsetWidth + getScrollbarWidth(targetEl),
          targetEl.offsetHeight
        )
      : breakpoint(options?.initialWidth ?? 0, options?.initialHeight ?? 0);
  });

  useLayoutEffect(() => {
    const targetEl: any =
      target && "current" in target ? target.current : target;
    if (!targetEl) return;

    const newState = breakpoint(
      // + getScrollbarWidth to prevent resize bonanza
      targetEl.offsetWidth + getScrollbarWidth(targetEl),
      targetEl.offsetHeight
    );
    if (!_.isEqual(state, newState)) setState(newState);
  }, [target]);

  // Where the magic happens
  useResizeObserver(target, (entry) => {
    const target = entry.target as HTMLElement;

    const newState = breakpoint(
      // + getScrollbarWidth to prevent resize bonanza
      target.offsetWidth + getScrollbarWidth(target),
      target.offsetHeight
    );
    if (!_.isEqual(state, newState)) setState(newState);
  });

  return state;
};

/**
 * A React hook for measuring the size of HTML elements including when they change,.
 * Returns a mobx observable.box value
 *
 * @param target A React ref created by `useRef()` or an HTML element
 * @param options Configures the initial width and initial height of the hook's state
 */
export const useBreakpointObservable = <T extends HTMLElement, F>(
  target: React.RefObject<T> | T | null,
  breakpoint: (width: number, height: number) => F,
  options?: UseBreakpointOptions
): IObservableValue<F> => {
  const state = useMemo(() => {
    const targetEl: any =
      target && "current" in target ? target.current : target;
    const initialValue = targetEl
      ? breakpoint(
          // + getScrollbarWidth to prevent resize bonanza
          targetEl.offsetWidth + getScrollbarWidth(targetEl),
          targetEl.offsetHeight
        )
      : breakpoint(options?.initialWidth ?? 0, options?.initialHeight ?? 0);

    return observable.box(initialValue);
  }, []);

  useLayoutEffect(() => {
    const targetEl: any =
      target && "current" in target ? target.current : target;
    if (!targetEl) return;

    const newState = breakpoint(
      // + getScrollbarWidth to prevent resize bonanza
      targetEl.offsetWidth + getScrollbarWidth(targetEl),
      targetEl.offsetHeight
    );
    if (!_.isEqual(state.get(), newState)) state.set(newState);
  }, [target]);

  // Where the magic happens
  useResizeObserver(target, (entry) => {
    const target = entry.target as HTMLElement;

    const newState = breakpoint(
      // + getScrollbarWidth to prevent resize bonanza
      target.offsetWidth + getScrollbarWidth(target),
      target.offsetHeight
    );
    if (!_.isEqual(state.get(), newState)) state.set(newState);
  });

  return state;
};

const getScrollbarWidth = (target: HTMLElement) => {
  return getScrollParent(target) ? getScrollBarWidth() : 0;
};

export interface UseBreakpointOptions {
  // The initial width to set into state.
  // This is useful for SSR environments.
  initialWidth?: number;
  // The initial height to set into state.
  // This is useful for SSR environments.
  initialHeight?: number;
}

export default useBreakpoint;

let _scrollbarWidth: number;
function getScrollBarWidth(): number {
  if (_scrollbarWidth === undefined) {
    let scrollDiv: HTMLElement = document.createElement("div");
    scrollDiv.style.setProperty("width", "100px");
    scrollDiv.style.setProperty("height", "100px");
    scrollDiv.style.setProperty("overflow", "scroll");
    scrollDiv.style.setProperty("position", "absolute");
    scrollDiv.style.setProperty("top", "-9999px");
    document.body.appendChild(scrollDiv);
    // Get the scrollbar width
    _scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;

    // Delete the DIV
    document.body.removeChild(scrollDiv);
  }

  return _scrollbarWidth;
}

function getScrollParent(node: HTMLElement) {
  if (!node) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getScrollParent(node.parentNode as HTMLElement);
  }
}
